import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
// Collapsible no longer used - chain log detail uses custom expand/collapse
import {
  ArrowLeft,
  CreditCard,
  DollarSign,
  Activity,
  Calendar,
  Gift,
  TrendingUp,
  Crown,
  ShieldCheck,
  User,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Plus,
  Minus,
  FileText,
  ChevronRight,
  Loader2,
  ArrowLeftIcon,
} from 'lucide-react'
import { adminApi } from '@/lib/api'
import { useToast } from '@/components/ui/use-toast'

interface UserDetail {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: 'User' | 'Editor' | 'Admin'
  status: 'PENDING_ACTIVATION' | 'ACTIVE' | 'SUSPENDED'
  inviteQuota: number
  createdAt: string
}

interface CreditStats {
  totalCreditsUsed: number
  creditsRemaining: number
  percentageUsed: number
  estimatedCost: number
  actualCost: number
  periodStart: Date
  periodEnd: Date
}

interface BillingCycle {
  id: string
  periodStart: string
  periodEnd: string
  creditLimit: number
  totalCreditsUsed: number
  estimatedCost: number
  actualCost: number
  bonusCredits: number
  intelligenceMode: string
  isCurrent: boolean
  createdAt: string
  activitiesCount: number
}

interface UsageActivity {
  id: string
  serviceType: string
  creditsUsed: number
  estimatedCost: number
  inputTokens?: number
  outputTokens?: number
  createdAt: string
  service?: {
    serviceType: string
    serviceIdentifier: string
    displayName: string
  }
}

interface CreditIncentive {
  id: string
  type: string
  credits: number
  reason: string
  status: string
  expiresAt?: string
  createdAt: string
}

interface SubscriptionTier {
  id: string
  tierName: string
  displayName: string
  monthlyCreditLimit: number
}

interface UserDetailData {
  user: UserDetail
  subscription?: {
    tier?: {
      id: string
      tierName: string
      displayName: string
    }
    paymentProvider?: string | null
    status?: string
  }
  currentStats: CreditStats | null
  recentLedgers: any[]
  activeIncentives: CreditIncentive[]
  recentTransactions: any[]
}

interface ChainLogConversation {
  conversationId: string
  logCount: number
  latestTimestamp: string | null
  hasError: boolean
}

interface ChainLogFile {
  filename: string
  size: number
  modified: string
  chainType?: string
  model?: string
  provider?: string
  startTime?: string
  endTime?: string
  entryCount?: number
  hasError?: boolean
  agentId?: string
  totalInputTokens?: number
  totalOutputTokens?: number
  tokensEstimated?: boolean
}

interface ChainLogStatus {
  chainLogEnabled: boolean
  globalEnabled: boolean
  effectivelyEnabled: boolean
  logCount: number
}

/**
 * A user's monthly spend cap as the server reports it.
 *
 * The server does the unit conversion rather than this screen, so the credit
 * figure beside a dollar cap can never be computed at a different rate than
 * the markup shown next to it.
 */
interface SpendLimitView {
  userId: string
  allowed: boolean
  capUsd: number | null
  spentUsd: number
  remainingUsd: number | null
  warn: boolean
  percentUsed: number | null
  configured?: { capUsd: number | null; enabled: boolean; warnAtPercent: number }
  // Optional on purpose. The frontend is rebuilt from `release/<slug>` the
  // moment a promotion PR merges, while the backend image takes another ten
  // minutes to build and roll, so for that window this page is talking to an
  // API that predates these fields. Reading them unguarded crashed the whole
  // user page, which is a worse failure than briefly missing an annotation.
  billing?: {
    effectiveMultiplier: number
    marginPercent: number
    atCost: boolean
    creditsPerDollar: number
  }
  credits?: {
    creditsPerDollar: number
    capCredits: number | null
    spentCredits: number | null
    remainingCredits: number | null
  }
  capProviderCostUsd?: number | null
}

/**
 * Money on this page is provider spend, which routinely runs to fractions of a
 * cent. Rounding to two places would show a real charge as $0.00, so small
 * amounts keep enough precision to be non-zero while ordinary ones stay
 * readable.
 */
function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-'
  if (value !== 0 && Math.abs(value) < 0.01) return `$${value.toFixed(6)}`
  return `$${value.toFixed(2)}`
}

function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-'
  // A fraction of a credit is still spend. Rounding it to "0 credits" beside a
  // non-zero dollar figure reads as "this person has used nothing", which is
  // the opposite of true, so small amounts keep their decimals.
  if (value !== 0 && Math.abs(value) < 10) {
    return `${value.toFixed(2)} credits`
  }
  return `${Math.round(value).toLocaleString()} credits`
}

function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [userDetail, setUserDetail] = useState<UserDetailData | null>(null)

  // Monthly spend cap (dollars, calendar month, enforced before the call).
  const [spendLimit, setSpendLimit] = useState<SpendLimitView | null>(null)
  const [capInput, setCapInput] = useState('')
  const [warnInput, setWarnInput] = useState('80')
  const [capEnabled, setCapEnabled] = useState(true)
  const [savingCap, setSavingCap] = useState(false)
  const [removingCap, setRemovingCap] = useState(false)
  const [billingHistory, setBillingHistory] = useState<BillingCycle[]>([])
  const [recentActivities, setRecentActivities] = useState<UsageActivity[]>([])
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview')
  
  // Credit assignment dialog state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')
  const [isBonusCredits, setIsBonusCredits] = useState(false)
  const [assigningCredits, setAssigningCredits] = useState(false)

  // Credit deduction dialog state
  const [deductDialogOpen, setDeductDialogOpen] = useState(false)
  const [deductAmount, setDeductAmount] = useState('')
  const [deductReason, setDeductReason] = useState('')
  const [deductingCredits, setDeductingCredits] = useState(false)

  // Tier management state
  const [availableTiers, setAvailableTiers] = useState<SubscriptionTier[]>([])
  const [selectedTierId, setSelectedTierId] = useState<string>('')
  const [updatingTier, setUpdatingTier] = useState(false)
  const [removingOverride, setRemovingOverride] = useState(false)

  // Chain log state (initialized from URL search params)
  const [chainLogStatus, setChainLogStatus] = useState<ChainLogStatus | null>(null)
  const [chainLogConversations, setChainLogConversations] = useState<ChainLogConversation[]>([])
  const [chainLogFiles, setChainLogFiles] = useState<ChainLogFile[]>([])
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(searchParams.get('conv'))
  const [selectedChainLog, setSelectedChainLog] = useState<any>(null)
  const [selectedChainFilename, setSelectedChainFilename] = useState<string | null>(searchParams.get('chain'))
  const [chainLogLoading, setChainLogLoading] = useState(false)
  const [togglingChainLog, setTogglingChainLog] = useState(false)
  const [entryTypeFilter, setEntryTypeFilter] = useState<string>('')
  const [entrySearchTerm, setEntrySearchTerm] = useState('')
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (userId) {
      loadUserDetail()
      loadTiers()
    }
  }, [userId])

  // Sync chain log state to URL search params
  useEffect(() => {
    const params: Record<string, string> = {}
    if (activeTab !== 'overview') params.tab = activeTab
    if (selectedConversationId) params.conv = selectedConversationId
    if (selectedChainFilename) params.chain = selectedChainFilename
    setSearchParams(params, { replace: true })
  }, [activeTab, selectedConversationId, selectedChainFilename])

  // Restore chain log state from URL on mount
  useEffect(() => {
    const tab = searchParams.get('tab')
    const conv = searchParams.get('conv')
    const chain = searchParams.get('chain')
    if (tab === 'chain-logs' && userId) {
      loadChainLogStatus()
      loadChainLogConversations()
      if (conv) {
        loadChainLogFiles(conv).then(() => {
          if (chain) loadChainLogDetail(chain)
        })
      }
    }
  }, [userId])

  const loadTiers = async () => {
    try {
      const tiers = await adminApi.getSubscriptionTiers()
      setAvailableTiers(Array.isArray(tiers) ? tiers : [])
    } catch (error) {
      console.error('Failed to load subscription tiers:', error)
    }
  }

  const handleSaveSpendLimit = async () => {
    if (!userId) return

    const trimmed = capInput.trim()
    // An empty box means "no cap", which is a different thing from zero: zero
    // is a real cap that blocks the user outright, and both are reachable.
    const cap = trimmed === '' ? null : Number(trimmed)
    if (cap !== null && (!Number.isFinite(cap) || cap < 0)) {
      toast({
        title: 'Invalid limit',
        description: 'Enter a dollar amount of 0 or more, or leave it blank for no limit.',
        variant: 'destructive',
      })
      return
    }

    const warn = Number(warnInput)
    if (!Number.isFinite(warn) || warn < 0 || warn > 100) {
      toast({
        title: 'Invalid warning threshold',
        description: 'The warning threshold must be a percentage between 0 and 100.',
        variant: 'destructive',
      })
      return
    }

    try {
      setSavingCap(true)
      await adminApi.setSpendLimit(userId, {
        monthlyCapUsd: cap,
        enabled: capEnabled,
        warnAtPercent: warn,
      })
      const refreshed = await adminApi.getSpendLimit(userId)
      setSpendLimit(refreshed)
      toast({
        title: 'Spend limit saved',
        description:
          cap === null
            ? 'This user has no monthly spend limit.'
            : `This user is limited to ${formatUsd(cap)} per calendar month.`,
      })
    } catch (error) {
      console.error('Failed to save spend limit:', error)
      toast({
        title: 'Error',
        description: 'Failed to save the spend limit. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSavingCap(false)
    }
  }

  const handleRemoveSpendLimit = async () => {
    if (!userId) return
    try {
      setRemovingCap(true)
      await adminApi.removeSpendLimit(userId)
      const refreshed = await adminApi.getSpendLimit(userId)
      setSpendLimit(refreshed)
      setCapInput('')
      setCapEnabled(true)
      toast({ title: 'Spend limit removed', description: 'This user is no longer capped.' })
    } catch (error) {
      console.error('Failed to remove spend limit:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove the spend limit. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setRemovingCap(false)
    }
  }

  const loadUserDetail = async () => {
    if (!userId) return
    
    try {
      setLoading(true)
      
      // Load user details, billing history, and recent activities in parallel
      const [detailResponse, billingResponse, activitiesResponse, limitResponse] = await Promise.all([
        adminApi.getUserCreditDetails(userId),
        adminApi.getUserBillingHistory(userId, { limit: 12 }).catch(() => ({ billingHistory: [] })),
        adminApi.getUserRecentActivities(userId, { limit: 50 }).catch(() => ({ activities: [] })),
        // Tolerated failure: an instance that predates cost pass-through has no
        // such endpoint, and the rest of this page must still render.
        adminApi.getSpendLimit(userId).catch(() => null)
      ])
      
      setUserDetail(detailResponse.data)
      setBillingHistory(billingResponse.billingHistory || [])
      setRecentActivities(activitiesResponse.activities || [])

      if (limitResponse) {
        setSpendLimit({
          ...limitResponse,
          configured: limitResponse.configured ?? {
            capUsd: limitResponse.capUsd ?? null,
            enabled: true,
            warnAtPercent: 80,
          },
        })
        setCapInput(limitResponse.configured?.capUsd != null ? String(limitResponse.configured.capUsd) : '')
        setWarnInput(String(limitResponse.configured?.warnAtPercent ?? 80))
        setCapEnabled(limitResponse.configured?.enabled ?? true)
      }

      // Set selected tier to current tier
      if (detailResponse.data?.subscription?.tier?.id) {
        setSelectedTierId(detailResponse.data.subscription.tier.id)
      }
    } catch (error) {
      console.error('Failed to load user detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGoBack = () => {
    const from = location.state?.from || '/users'
    navigate(from, { state: { from: `/users/${userId}`, direction: 'pop' } })
  }

  const handleAssignCredits = async () => {
    if (!userId || !creditAmount || parseFloat(creditAmount) <= 0) {
      toast({
        title: 'Invalid Input',
        description: 'Please enter a valid credit amount greater than 0',
        variant: 'destructive',
      })
      return
    }

    try {
      setAssigningCredits(true)
      
      console.log('Calling adminApi.assignCredits', {
        userId,
        credits: parseFloat(creditAmount),
        reason: creditReason || 'Admin credit assignment',
        isBonus: isBonusCredits,
      });
      
      await adminApi.assignCredits(userId, {
        credits: parseFloat(creditAmount),
        reason: creditReason || 'Admin credit assignment',
        isBonus: isBonusCredits,
      })
      
      console.log('Credit assignment successful', { creditAmount, isBonusCredits });
      
      toast({
        title: 'Credits Assigned',
        description: `Successfully assigned ${creditAmount} ${isBonusCredits ? 'bonus ' : ''}credits to the user`,
      })
      
      // Reset form and close dialog
      setCreditAmount('')
      setCreditReason('')
      setIsBonusCredits(false)
      setCreditDialogOpen(false)
      
      // Reload user details to reflect the change
      console.log('Reloading user details...');
      await loadUserDetail()
      console.log('User details reloaded');
    } catch (error) {
      console.error('Failed to assign credits:', error)
      toast({
        title: 'Error',
        description: 'Failed to assign credits. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setAssigningCredits(false)
    }
  }

  const handleDeductCredits = async () => {
    if (!userId || !deductAmount || parseFloat(deductAmount) <= 0) {
      toast({
        title: 'Invalid Input',
        description: 'Please enter a valid credit amount greater than 0',
        variant: 'destructive',
      })
      return
    }

    try {
      setDeductingCredits(true)

      await adminApi.deductCredits(userId, {
        credits: parseFloat(deductAmount),
        reason: deductReason || 'Admin credit deduction (testing)',
      })

      toast({
        title: 'Credits Deducted',
        description: `Successfully deducted ${deductAmount} credits from the user`,
      })

      // Reset form and close dialog
      setDeductAmount('')
      setDeductReason('')
      setDeductDialogOpen(false)

      // Reload user details to reflect the change
      await loadUserDetail()
    } catch (error) {
      console.error('Failed to deduct credits:', error)
      toast({
        title: 'Error',
        description: 'Failed to deduct credits. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setDeductingCredits(false)
    }
  }

  const handleUpdateTier = async () => {
    if (!userId || !selectedTierId) {
      toast({
        title: 'Invalid Selection',
        description: 'Please select a subscription tier',
        variant: 'destructive',
      })
      return
    }

    // Check if tier has actually changed
    const currentTierId = userDetail?.subscription?.tier?.id
    if (currentTierId === selectedTierId) {
      toast({
        title: 'No Change',
        description: 'Selected tier is the same as current tier',
      })
      return
    }

    try {
      setUpdatingTier(true)

      await adminApi.adjustSubscription(userId, selectedTierId)

      const newTier = availableTiers.find(t => t.id === selectedTierId)
      toast({
        title: 'Tier Updated',
        description: `Successfully updated subscription to ${newTier?.displayName || 'new tier'}`,
      })

      // Reload user details to reflect the change
      await loadUserDetail()
    } catch (error) {
      console.error('Failed to update tier:', error)
      toast({
        title: 'Error',
        description: 'Failed to update subscription tier. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setUpdatingTier(false)
    }
  }

  const handleRemoveTierOverride = async () => {
    if (!userId) return

    try {
      setRemovingOverride(true)

      await adminApi.removeTierOverride(userId)

      toast({
        title: 'Override Removed',
        description: 'User reverted to free tier. RevenueCat can now manage their subscription.',
      })

      // Reload user details to reflect the change
      await loadUserDetail()
    } catch (error) {
      console.error('Failed to remove tier override:', error)
      toast({
        title: 'Error',
        description: 'Failed to remove tier override. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setRemovingOverride(false)
    }
  }

  // Chain log functions
  const loadChainLogStatus = async () => {
    if (!userId) return
    try {
      const status = await adminApi.getUserChainLogStatus(userId)
      setChainLogStatus(status)
    } catch (error) {
      console.error('Failed to load chain log status:', error)
    }
  }

  const loadChainLogConversations = async () => {
    if (!userId) return
    try {
      setChainLogLoading(true)
      const data = await adminApi.getUserChainLogConversations(userId)
      setChainLogConversations(data.conversations || [])
    } catch (error) {
      console.error('Failed to load chain log conversations:', error)
    } finally {
      setChainLogLoading(false)
    }
  }

  const loadChainLogFiles = async (conversationId: string) => {
    if (!userId) return
    try {
      setChainLogLoading(true)
      setSelectedConversationId(conversationId)
      setSelectedChainLog(null)
      setSelectedChainFilename(null)
      const data = await adminApi.getUserChainLogs(userId, conversationId)
      setChainLogFiles(data.logs || [])
    } catch (error) {
      console.error('Failed to load chain log files:', error)
    } finally {
      setChainLogLoading(false)
    }
  }

  const loadChainLogDetail = async (filename: string) => {
    if (!userId) return
    const convId = selectedConversationId
    if (!convId) return
    try {
      setChainLogLoading(true)
      setSelectedChainFilename(filename)
      setExpandedEntries(new Set())
      setEntryTypeFilter('')
      setEntrySearchTerm('')
      const data = await adminApi.getChainLog(userId, convId, filename)
      setSelectedChainLog(data)
    } catch (error) {
      console.error('Failed to load chain log detail:', error)
    } finally {
      setChainLogLoading(false)
    }
  }

  const handleToggleChainLogging = async () => {
    if (!userId || !chainLogStatus) return
    try {
      setTogglingChainLog(true)
      const newEnabled = !chainLogStatus.chainLogEnabled
      await adminApi.toggleUserChainLogging(userId, newEnabled)
      setChainLogStatus(prev => prev ? { ...prev, chainLogEnabled: newEnabled, effectivelyEnabled: prev.globalEnabled || newEnabled } : null)
      toast({
        title: newEnabled ? 'Chain logging enabled' : 'Chain logging disabled',
        description: `Chain logging has been ${newEnabled ? 'enabled' : 'disabled'} for this user`,
      })
    } catch (error) {
      console.error('Failed to toggle chain logging:', error)
      toast({ title: 'Error', description: 'Failed to toggle chain logging', variant: 'destructive' })
    } finally {
      setTogglingChainLog(false)
    }
  }

  const getEntryTypeBadgeStyle = (type: string) => {
    switch (type) {
      case 'start': return 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50'
      case 'llm_input': return 'bg-indigo-900/30 text-indigo-300 border-indigo-700/50'
      case 'llm_output': return 'bg-rose-900/30 text-rose-300 border-rose-700/50'
      case 'tag_extracted': return 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50'
      case 'action': return 'bg-cyan-900/30 text-cyan-300 border-cyan-700/50'
      case 'stream_chunk': return 'bg-gray-800/50 text-gray-400 border-gray-700/50'
      case 'stream_timing': return 'bg-violet-900/30 text-violet-300 border-violet-700/50'
      case 'performance_breakdown': return 'bg-teal-900/30 text-teal-300 border-teal-700/50'
      case 'complete': return 'bg-emerald-900/30 text-emerald-300 border-emerald-700/50'
      case 'error': return 'bg-red-900/40 text-red-300 border-red-700/50'
      default: return 'bg-gray-800/50 text-gray-400 border-gray-700/50'
    }
  }

  const extractXMLTags = (content: string): Record<string, string> => {
    const tags: Record<string, string> = {}
    const tagPatterns = [
      'reply', 'thought', 'newArtifact', 'artifactUpdate',
      'behaviorUpdate', 'schedule', 'knowledgeSearch',
      'conversationName', 'toolCall', 'surface', 'outputInstructions',
      'surfaceAction', 'getSurfaces', 'pinArtifact', 'unpinArtifact',
      'updateLayout', 'cliAgent'
    ]
    tagPatterns.forEach(tagName => {
      const regex = new RegExp(`<${tagName}[^>]*>([\\s\\S]*?)<\\/${tagName}>`, 'gi')
      const matches = content.match(regex)
      if (matches) {
        matches.forEach((match, index) => {
          const contentMatch = match.match(new RegExp(`<${tagName}[^>]*>([\\s\\S]*?)<\\/${tagName}>`, 'i'))
          if (contentMatch?.[1]) {
            const key = matches.length > 1 ? `${tagName}_${index + 1}` : tagName
            tags[key] = contentMatch[1].trim()
          }
        })
      }
    })
    return tags
  }

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`
    if (ms < 10000) return `${(ms / 1000).toFixed(2)}s`
    return `${(ms / 1000).toFixed(1)}s`
  }

  const formatDurationMs = (startTime: string, endTime?: string) => {
    if (!endTime) return 'ongoing'
    const ms = new Date(endTime).getTime() - new Date(startTime).getTime()
    return formatMs(ms)
  }

  const truncateStr = (str: string, len = 200) => {
    if (!str) return ''
    const clean = str.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
    return clean.length <= len ? clean : clean.substring(0, len) + '...'
  }

  const toggleEntry = (index: number) => {
    setExpandedEntries(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const filteredEntries = useMemo(() => {
    if (!selectedChainLog?.entries) return []
    return selectedChainLog.entries.filter((entry: any) => {
      if (entryTypeFilter && entry.type !== entryTypeFilter) return false
      if (entrySearchTerm && !JSON.stringify(entry).toLowerCase().includes(entrySearchTerm.toLowerCase())) return false
      return true
    })
  }, [selectedChainLog, entryTypeFilter, entrySearchTerm])

  const chainTokenTotals = useMemo(() => {
    if (!selectedChainLog?.entries) return { input: 0, output: 0, estimated: false }
    let input = 0, output = 0
    let hasActual = false
    for (const entry of selectedChainLog.entries) {
      if (entry.tokenCounts?.input) { input += entry.tokenCounts.input; hasActual = true }
      if (entry.tokenCounts?.output) { output += entry.tokenCounts.output; hasActual = true }
    }
    // Fallback: estimate tokens from content when no actual counts available
    if (!hasActual) {
      for (const entry of selectedChainLog.entries) {
        if (entry.type === 'llm_input' && entry.data?.messages) {
          const content = entry.data.messages.map((m: any) => m.content || '').join(' ')
          input += Math.ceil(content.length / 4)
        } else if (entry.type === 'llm_output' && entry.data?.response) {
          output += Math.ceil(entry.data.response.length / 4)
        }
      }
    }
    return { input, output, estimated: !hasActual && (input > 0 || output > 0) }
  }, [selectedChainLog])

  const renderEntryContent = (entry: any, index: number) => {
    const isExpanded = expandedEntries.has(index)
    switch (entry.type) {
      case 'start':
        return (
          <div className="space-y-1 text-sm text-gray-300">
            <div><span className="text-gray-500">User:</span> {entry.data.context?.userEmail || 'N/A'}</div>
            <div><span className="text-gray-500">Agent:</span> {entry.data.context?.agentName || 'N/A'}</div>
            {entry.data.context?.messageContent && (
              <div className="bg-gray-900 rounded p-2 mt-1 text-xs font-mono text-gray-300 whitespace-pre-wrap">
                {truncateStr(entry.data.context.messageContent, isExpanded ? 10000 : 300)}
                {!isExpanded && entry.data.context.messageContent.length > 300 && (
                  <button onClick={() => toggleEntry(index)} className="block mt-1 text-blue-400 hover:text-blue-300 text-xs">Show more</button>
                )}
              </div>
            )}
          </div>
        )
      case 'llm_input':
        return (
          <div className="space-y-2 text-sm">
            <div className="text-gray-400">Messages: {entry.data.messageCount}</div>
            {entry.data.messages?.map((msg: any, i: number) => {
              const msgKey = `${index}-${i}`
              const isMsgExpanded = expandedEntries.has(index * 1000 + i)
              return (
                <div key={msgKey} className="mt-1">
                  <div className="text-xs text-gray-500 mb-1 font-medium">[{msg.type}]</div>
                  <div className={`bg-gray-900 rounded p-2 text-xs font-mono text-gray-300 whitespace-pre-wrap ${!isMsgExpanded ? 'max-h-48 overflow-hidden relative' : ''}`}>
                    {msg.content}
                    {!isMsgExpanded && msg.content?.length > 500 && (
                      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-transparent" />
                    )}
                  </div>
                  {msg.content?.length > 500 && (
                    <button onClick={() => toggleEntry(index * 1000 + i)} className="text-blue-400 hover:text-blue-300 text-xs mt-1">
                      {isMsgExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )
      case 'llm_output': {
        const xmlTags = extractXMLTags(entry.data.response || '')
        const hasXml = Object.keys(xmlTags).length > 0
        const isRawExpanded = expandedEntries.has(index * 10000)
        return (
          <div className="space-y-2 text-sm">
            {hasXml ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-xs font-medium">XML Tags Found:</span>
                  <button onClick={() => toggleEntry(index * 10000)} className="text-xs text-blue-400 hover:text-blue-300 px-2 py-0.5 rounded bg-gray-800">
                    {isRawExpanded ? 'Hide Raw' : 'View Raw'}
                  </button>
                </div>
                {isRawExpanded && (
                  <pre className="bg-gray-900 rounded p-2 text-xs font-mono text-gray-400 whitespace-pre-wrap max-h-96 overflow-auto">
                    {entry.data.response}
                  </pre>
                )}
                {Object.entries(xmlTags).map(([tag, content]) => (
                  <div key={tag} className="border-l-2 border-blue-500 pl-3 ml-1">
                    <div className="text-xs text-blue-400 font-mono mb-1">&lt;{tag}&gt;</div>
                    <div className="bg-gray-900 rounded p-2 text-xs font-mono text-gray-300 whitespace-pre-wrap max-h-64 overflow-auto">
                      {content}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className={`bg-gray-900 rounded p-2 text-xs font-mono text-gray-300 whitespace-pre-wrap ${!isExpanded ? 'max-h-48 overflow-hidden relative' : ''}`}>
                {entry.data.response}
                {!isExpanded && entry.data.response?.length > 500 && (
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-transparent" />
                )}
              </div>
            )}
            {!hasXml && entry.data.response?.length > 500 && (
              <button onClick={() => toggleEntry(index)} className="text-blue-400 hover:text-blue-300 text-xs">
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )
      }
      case 'error':
        return (
          <div className="bg-red-950/50 border border-red-900/50 rounded p-2 text-sm text-red-300">
            {entry.data.error?.message || JSON.stringify(entry.data.error)}
          </div>
        )
      case 'action':
        return (
          <div className="space-y-1 text-sm">
            <div className="text-emerald-300 font-medium">{entry.data.action}</div>
            {entry.data.details && (
              <pre className="bg-gray-900 rounded p-2 text-xs font-mono text-gray-400 whitespace-pre-wrap max-h-48 overflow-auto">
                {typeof entry.data.details === 'string' ? entry.data.details : JSON.stringify(entry.data.details, null, 2)}
              </pre>
            )}
          </div>
        )
      case 'stream_chunk':
        return (
          <div className="text-xs text-gray-500">
            seq={entry.data.sequence}, length={entry.data.contentLength}, final={String(entry.data.isFinalChunk)}
          </div>
        )
      case 'stream_timing':
        return (
          <div className="space-y-2">
            <div className="text-sm text-gray-400">Stage: {entry.data.stage || 'Unknown'}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {entry.data.totalStreamingTime != null && (
                <div className="bg-gray-900 border border-gray-800 rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Total Streaming</div>
                  <div className="text-sm font-semibold text-blue-400">{formatMs(entry.data.totalStreamingTime)}</div>
                </div>
              )}
              {entry.data.finalCompletionTime != null && (
                <div className="bg-gray-900 border border-gray-800 rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Total Time</div>
                  <div className="text-sm font-semibold text-blue-400">{formatMs(entry.data.finalCompletionTime)}</div>
                </div>
              )}
              {entry.data.chunkProcessTime != null && (
                <div className="bg-gray-900 border border-gray-800 rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Chunk Process</div>
                  <div className="text-sm font-semibold text-gray-300">{formatMs(entry.data.chunkProcessTime)}</div>
                </div>
              )}
              {entry.data.parseTime != null && (
                <div className="bg-gray-900 border border-gray-800 rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">Parse Time</div>
                  <div className="text-sm font-semibold text-gray-300">{formatMs(entry.data.parseTime)}</div>
                </div>
              )}
            </div>
          </div>
        )
      case 'performance_breakdown': {
        const summary = entry.data.summary as Record<string, number> | undefined
        const phases = entry.data.phases as Array<{ name: string; duration: number; subPhases?: Array<{ name: string; duration: number }> }> | undefined
        return (
          <div className="space-y-3">
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(summary).filter(([, v]) => (v as number) > 0).map(([key, value]) => {
                  const ms = value as number
                  const color = ms > 5000 ? 'text-red-400' : ms > 2000 ? 'text-amber-400' : 'text-emerald-400'
                  return (
                    <div key={key} className="bg-gray-900 border border-gray-800 rounded p-2 text-center">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{key.replace(/Ms$/, '').replace(/([A-Z])/g, ' $1').trim()}</div>
                      <div className={`text-sm font-semibold ${color}`}>{formatMs(ms)}</div>
                    </div>
                  )
                })}
              </div>
            )}
            {phases && phases.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded p-3">
                <div className="text-xs font-medium text-gray-400 mb-2 border-b border-gray-800 pb-1">Execution Phases</div>
                {phases.map((phase, i) => (
                  <div key={i}>
                    <div className="flex justify-between py-1 border-b border-gray-800/50 text-xs">
                      <span className="text-gray-400">{phase.name}</span>
                      <span className={`font-medium ${phase.duration > 5000 ? 'text-red-400' : phase.duration > 2000 ? 'text-amber-400' : 'text-gray-400'}`}>{formatMs(phase.duration || 0)}</span>
                    </div>
                    {phase.subPhases?.map((sub, j) => (
                      <div key={j} className="flex justify-between py-0.5 pl-4 text-[11px] border-b border-gray-900/50">
                        <span className="text-gray-600">{sub.name}</span>
                        <span className="text-gray-600">{formatMs(sub.duration || 0)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      }
      case 'tag_extracted':
        return (
          <div className="text-sm text-yellow-300">
            Tags: {Object.keys(entry.data.tags || {}).join(', ')}
          </div>
        )
      case 'complete':
        return (
          <div className="space-y-1 text-sm">
            <div className={entry.data.success ? 'text-emerald-400' : 'text-red-400'}>
              {entry.data.success ? 'Success' : 'Failed'}
            </div>
            {entry.data.content && (
              <div className="text-gray-500 text-xs">Content length: {entry.data.content.length}</div>
            )}
          </div>
        )
      default:
        return (
          <pre className="bg-gray-900 rounded p-2 text-xs font-mono text-gray-400 whitespace-pre-wrap max-h-48 overflow-auto">
            {JSON.stringify(entry.data, null, 2)}
          </pre>
        )
    }
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        )
      case 'PENDING_ACTIVATION':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        )
      case 'SUSPENDED':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Suspended
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  // Get role badge
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'Admin':
        return (
          <Badge variant="default" className="bg-purple-100 text-purple-800">
            <Crown className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        )
      case 'Editor':
        return (
          <Badge variant="outline" className="border-blue-300 text-blue-800">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Editor
          </Badge>
        )
      case 'User':
        return (
          <Badge variant="secondary">
            <User className="h-3 w-3 mr-1" />
            User
          </Badge>
        )
      default:
        return <Badge variant="outline">{role}</Badge>
    }
  }

  const getDisplayName = (user: UserDetail) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email.split('@')[0]
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!userDetail) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">User not found</h3>
              <p className="text-muted-foreground">The requested user could not be found.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { user, currentStats, activeIncentives } = userDetail

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{getDisplayName(user)}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          {getStatusBadge(user.status)}
          {getRoleBadge(user.role)}
        </div>
      </div>

      {/* Stats Cards */}
      {currentStats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Credits Remaining</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(currentStats.creditsRemaining ?? 0).toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {Number(currentStats.percentageUsed || 0).toFixed(1)}% used this cycle
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(currentStats.totalCreditsUsed ?? 0).toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Current billing cycle
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated Cost</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${Number(currentStats.estimatedCost || 0).toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">
                {Number(currentStats.actualCost) > 0 && `Actual: $${Number(currentStats.actualCost).toFixed(4)}`}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Incentives</CardTitle>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeIncentives.length}</div>
              <p className="text-xs text-muted-foreground">
                Bonus credits active
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Information */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="billing-history">Billing History</TabsTrigger>
          <TabsTrigger value="recent-activities">Recent Activities</TabsTrigger>
          <TabsTrigger value="incentives">Incentives</TabsTrigger>
          <TabsTrigger value="chain-logs" onClick={() => { if (!chainLogStatus) loadChainLogStatus(); loadChainLogConversations(); }}>Chain Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>User Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  <span>{user.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Name:</span>
                  <span>{getDisplayName(user)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Role:</span>
                  <span>{getRoleBadge(user.role)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span>{getStatusBadge(user.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Invite Quota:</span>
                  <span>{user.inviteQuota || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Joined:</span>
                  <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Tier Management */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription Tier</CardTitle>
                <CardDescription>
                  Manage this user's subscription tier
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Current Tier:</span>
                  <Badge variant="outline">
                    {userDetail?.subscription?.tier?.displayName || 'No subscription'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Provider:</span>
                  <Badge variant={userDetail?.subscription?.paymentProvider === 'manual' ? 'secondary' : 'outline'}>
                    {userDetail?.subscription?.paymentProvider === 'manual'
                      ? 'Admin Override'
                      : userDetail?.subscription?.paymentProvider || 'None'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tier-select">Change Tier</Label>
                  <Select value={selectedTierId} onValueChange={setSelectedTierId}>
                    <SelectTrigger id="tier-select">
                      <SelectValue placeholder="Select a tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTiers.map((tier) => (
                        <SelectItem key={tier.id} value={tier.id}>
                          {tier.displayName} ({tier.monthlyCreditLimit.toLocaleString()} credits/mo)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleUpdateTier}
                  disabled={updatingTier || !selectedTierId || selectedTierId === userDetail?.subscription?.tier?.id}
                  className="w-full"
                >
                  {updatingTier ? 'Updating...' : 'Update Tier'}
                </Button>
                {userDetail?.subscription?.paymentProvider === 'manual' && (
                  <Button
                    variant="outline"
                    onClick={handleRemoveTierOverride}
                    disabled={removingOverride}
                    className="w-full"
                  >
                    {removingOverride ? 'Removing Override...' : 'Remove Admin Override'}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/*
              Monthly spend cap. Deliberately its own card next to the tier
              rather than folded into it: a tier is an allowance of credits
              granted each period, a cap is a hard refusal in dollars checked
              before the request reaches the provider. They are different
              controls and an admin needs to see which one is doing the work.
            */}
            {spendLimit && (
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Spend Limit</CardTitle>
                  <CardDescription>
                    A hard cap in dollars on the calendar month. Requests are refused once it is
                    reached, rather than reported afterwards.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    {spendLimit.configured?.capUsd == null ? (
                      <Badge variant="outline">No limit</Badge>
                    ) : !spendLimit.configured?.enabled ? (
                      <Badge variant="secondary">Limit suspended</Badge>
                    ) : spendLimit.allowed ? (
                      <Badge variant={spendLimit.warn ? 'secondary' : 'outline'}>
                        {spendLimit.warn ? 'Near limit' : 'Within limit'}
                      </Badge>
                    ) : (
                      <Badge variant="destructive">Limit reached</Badge>
                    )}
                  </div>

                  <div className="flex justify-between">
                    <span className="font-medium">Spent this month:</span>
                    <span>
                      {formatUsd(spendLimit.spentUsd)}
                      {spendLimit.credits && (
                        <span className="text-muted-foreground ml-2">
                          ({formatCredits(spendLimit.credits.spentCredits)})
                        </span>
                      )}
                    </span>
                  </div>

                  {spendLimit.configured?.capUsd != null && (
                    <>
                      <div className="flex justify-between">
                        <span className="font-medium">Limit:</span>
                        <span>
                          {formatUsd(spendLimit.configured?.capUsd)}
                          {spendLimit.credits && (
                            <span className="text-muted-foreground ml-2">
                              ({formatCredits(spendLimit.credits.capCredits)})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Remaining:</span>
                        <span>
                          {formatUsd(spendLimit.remainingUsd)}
                          {spendLimit.credits && (
                            <span className="text-muted-foreground ml-2">
                              ({formatCredits(spendLimit.credits.remainingCredits)})
                            </span>
                          )}
                        </span>
                      </div>
                    </>
                  )}

                  {/*
                    The markup is shown beside the figures rather than on a
                    settings page elsewhere, because a cap in credits is
                    meaningless without it: a credit is a unit of marked-up
                    revenue, so the same cap buys different amounts of real
                    provider capacity at different multipliers.
                  */}
                  {spendLimit.billing && (
                  <div className="rounded-md border p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">Markup in effect:</span>
                      {spendLimit.billing.atCost ? (
                        <Badge variant="outline">At cost (no margin)</Badge>
                      ) : (
                        <span>
                          {spendLimit.billing.marginPercent.toFixed(0)}% (x
                          {spendLimit.billing.effectiveMultiplier})
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium">Credit rate:</span>
                      <span>{spendLimit.billing.creditsPerDollar} credits per $1 billed</span>
                    </div>
                    {spendLimit.configured?.capUsd != null && (
                      <div className="flex justify-between">
                        <span className="font-medium">Provider cost at limit:</span>
                        <span>{formatUsd(spendLimit.capProviderCostUsd)}</span>
                      </div>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {spendLimit.billing.atCost
                        ? 'This org is billed exactly what the provider charged, so the limit is the real spend.'
                        : 'The limit is in billed dollars. Provider cost at the limit is what it actually costs us.'}
                    </p>
                  </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="cap-input">Monthly limit (USD)</Label>
                    <Input
                      id="cap-input"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Blank for no limit"
                      value={capInput}
                      onChange={(e) => setCapInput(e.target.value)}
                    />
                    {spendLimit.billing && capInput.trim() !== '' && Number.isFinite(Number(capInput)) && (
                      <p className="text-muted-foreground text-xs">
                        {formatCredits(Number(capInput) * spendLimit.billing.creditsPerDollar)}
                        {!spendLimit.billing.atCost &&
                          `, costing ${formatUsd(
                            Number(capInput) / spendLimit.billing.effectiveMultiplier,
                          )} of provider spend`}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="warn-input">Warn at (% of limit)</Label>
                    <Input
                      id="warn-input"
                      type="number"
                      min="0"
                      max="100"
                      value={warnInput}
                      onChange={(e) => setWarnInput(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="cap-enabled">Limit active</Label>
                    <Switch id="cap-enabled" checked={capEnabled} onCheckedChange={setCapEnabled} />
                  </div>

                  <Button onClick={handleSaveSpendLimit} disabled={savingCap} className="w-full">
                    {savingCap ? 'Saving...' : 'Save Limit'}
                  </Button>

                  {spendLimit.configured?.capUsd != null && (
                    <Button
                      variant="outline"
                      onClick={handleRemoveSpendLimit}
                      disabled={removingCap}
                      className="w-full"
                    >
                      {removingCap ? 'Removing...' : 'Remove Limit'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {currentStats && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Current Billing Cycle</CardTitle>
                    <CardDescription>
                      {new Date(currentStats.periodStart).toLocaleDateString()} - {new Date(currentStats.periodEnd).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                            Assign Credits
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assign Credits</DialogTitle>
                            <DialogDescription>
                              Add credits to this user's current billing cycle
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="credits" className="text-right">
                                Credits
                              </Label>
                              <Input
                                id="credits"
                                type="number"
                                value={creditAmount}
                                onChange={(e) => setCreditAmount(e.target.value)}
                                placeholder="Enter credit amount"
                                className="col-span-3"
                                min="1"
                                step="100"
                              />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="reason" className="text-right">
                                Reason
                              </Label>
                              <Textarea
                                id="reason"
                                value={creditReason}
                                onChange={(e) => setCreditReason(e.target.value)}
                                placeholder="Optional: Reason for credit assignment"
                                className="col-span-3"
                                rows={3}
                              />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="bonus" className="text-right">
                                Bonus Credits
                              </Label>
                              <div className="col-span-3 flex items-center space-x-2">
                                <Switch
                                  id="bonus"
                                  checked={isBonusCredits}
                                  onCheckedChange={setIsBonusCredits}
                                />
                                <Label htmlFor="bonus" className="font-normal">
                                  Add to bonus credit pool (non-expiring)
                                </Label>
                              </div>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setCreditDialogOpen(false)}
                              disabled={assigningCredits}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleAssignCredits}
                              disabled={assigningCredits || !creditAmount || parseFloat(creditAmount) <= 0}
                            >
                              {assigningCredits ? 'Assigning...' : 'Assign Credits'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      <Dialog open={deductDialogOpen} onOpenChange={setDeductDialogOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Minus className="h-4 w-4 mr-2" />
                            Deduct Credits
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Deduct Credits</DialogTitle>
                            <DialogDescription>
                              Remove credits from this user's current billing cycle (for testing purposes)
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="deduct-credits" className="text-right">
                                Credits
                              </Label>
                              <Input
                                id="deduct-credits"
                                type="number"
                                value={deductAmount}
                                onChange={(e) => setDeductAmount(e.target.value)}
                                placeholder="Enter credit amount to deduct"
                                className="col-span-3"
                                min="1"
                                step="100"
                              />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="deduct-reason" className="text-right">
                                Reason
                              </Label>
                              <Textarea
                                id="deduct-reason"
                                value={deductReason}
                                onChange={(e) => setDeductReason(e.target.value)}
                                placeholder="Optional: Reason for credit deduction"
                                className="col-span-3"
                                rows={3}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              variant="outline"
                              onClick={() => setDeductDialogOpen(false)}
                              disabled={deductingCredits}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={handleDeductCredits}
                              disabled={deductingCredits || !deductAmount || parseFloat(deductAmount) <= 0}
                            >
                              {deductingCredits ? 'Deducting...' : 'Deduct Credits'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium">Credits Used:</span>
                    <span>{(currentStats.totalCreditsUsed ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Credits Remaining:</span>
                    <span>{(currentStats.creditsRemaining ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Bonus Credits:</span>
                    <span>{(billingHistory.find(c => c.isCurrent)?.bonusCredits || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Usage %:</span>
                    <span>{Number(currentStats.percentageUsed || 0).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Estimated Cost:</span>
                    <span>${Number(currentStats.estimatedCost || 0).toFixed(4)}</span>
                  </div>
                  {currentStats.actualCost > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Actual Cost:</span>
                      <span>${Number(currentStats.actualCost || 0).toFixed(4)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
        </TabsContent>

        <TabsContent value="billing-history">
          <Card>
            <CardHeader>
              <CardTitle>Billing History</CardTitle>
              <CardDescription>Historical billing cycles and credit usage</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Credit Limit</TableHead>
                    <TableHead>Credits Used</TableHead>
                    <TableHead>Bonus Credits</TableHead>
                    <TableHead>Estimated Cost</TableHead>
                    <TableHead>Actual Cost</TableHead>
                    <TableHead>Activities</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingHistory.map((cycle) => (
                    <TableRow key={cycle.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {new Date(cycle.periodStart).toLocaleDateString()} - {new Date(cycle.periodEnd).toLocaleDateString()}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{cycle.creditLimit.toLocaleString()}</TableCell>
                      <TableCell>{cycle.totalCreditsUsed.toLocaleString()}</TableCell>
                      <TableCell>{(cycle.bonusCredits || 0).toLocaleString()}</TableCell>
                      <TableCell>${Number(cycle.estimatedCost || 0).toFixed(4)}</TableCell>
                      <TableCell>${Number(cycle.actualCost || 0).toFixed(4)}</TableCell>
                      <TableCell>{cycle.activitiesCount}</TableCell>
                      <TableCell>
                        {cycle.isCurrent ? (
                          <Badge variant="default">Current</Badge>
                        ) : (
                          <Badge variant="secondary">Completed</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {billingHistory.length === 0 && (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No billing history</h3>
                  <p className="text-muted-foreground">This user has no billing cycles yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent-activities">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activities</CardTitle>
              <CardDescription>Latest usage activities and credit consumption</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Credits Used</TableHead>
                    <TableHead>Estimated Cost</TableHead>
                    <TableHead>Tokens</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentActivities.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {activity.service?.displayName || activity.serviceType}
                          </div>
                          {activity.service?.serviceIdentifier && (
                            <div className="text-sm text-muted-foreground">
                              {activity.service.serviceIdentifier}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{activity.creditsUsed.toLocaleString()}</TableCell>
                      <TableCell>${Number(activity.estimatedCost || 0).toFixed(4)}</TableCell>
                      <TableCell>
                        {activity.inputTokens || activity.outputTokens ? (
                          <div className="text-sm">
                            {activity.inputTokens && `${activity.inputTokens.toLocaleString()} in`}
                            {activity.inputTokens && activity.outputTokens && ' / '}
                            {activity.outputTokens && `${activity.outputTokens.toLocaleString()} out`}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{new Date(activity.createdAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {recentActivities.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No recent activities</h3>
                  <p className="text-muted-foreground">This user has no recent usage activities.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incentives">
          <Card>
            <CardHeader>
              <CardTitle>Active Incentives</CardTitle>
              <CardDescription>Bonus credits and special offers</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeIncentives.map((incentive) => (
                    <TableRow key={incentive.id}>
                      <TableCell>
                        <Badge variant="outline">{incentive.type}</Badge>
                      </TableCell>
                      <TableCell>{(incentive.credits || 0).toLocaleString()}</TableCell>
                      <TableCell>{incentive.reason}</TableCell>
                      <TableCell>
                        <Badge variant={incentive.status === 'active' ? 'default' : 'secondary'}>
                          {incentive.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {incentive.expiresAt ? new Date(incentive.expiresAt).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell>{new Date(incentive.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {activeIncentives.length === 0 && (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No active incentives</h3>
                  <p className="text-muted-foreground">This user has no active credit incentives.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chain-logs" className="space-y-4">
          {/* Toggle and status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Chain Logging</CardTitle>
                  <CardDescription>
                    Enable detailed agent chain logging for this user
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  {chainLogStatus?.globalEnabled && (
                    <Badge variant="secondary">Global override active</Badge>
                  )}
                  {chainLogStatus && (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={chainLogStatus.chainLogEnabled}
                        onCheckedChange={handleToggleChainLogging}
                        disabled={togglingChainLog}
                      />
                      <Label>{chainLogStatus.chainLogEnabled ? 'Enabled' : 'Disabled'}</Label>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            {chainLogStatus && (
              <CardContent>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>Total logs: {chainLogStatus.logCount}</span>
                  <span>Effectively {chainLogStatus.effectivelyEnabled ? 'active' : 'inactive'}</span>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Chain log browser */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    {selectedChainLog ? 'Chain Log Detail' : selectedConversationId ? 'Chain Logs' : 'Conversations'}
                  </CardTitle>
                  <CardDescription>
                    {selectedChainLog
                      ? `${selectedChainLog.chainType || 'standard'} chain — ${selectedChainLog.model || 'unknown model'}`
                      : selectedConversationId
                        ? `Conversation: ${selectedConversationId.substring(0, 8)}...`
                        : 'Select a conversation to browse chain logs'}
                  </CardDescription>
                </div>
                {(selectedConversationId || selectedChainLog) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (selectedChainLog) {
                        setSelectedChainLog(null)
                        setSelectedChainFilename(null)
                      } else {
                        setSelectedConversationId(null)
                        setSelectedChainFilename(null)
                        setChainLogFiles([])
                      }
                    }}
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {chainLogLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : selectedChainLog ? (
                /* Detail view - dark theme matching chain-log-viewer */
                <div className="bg-[#1a1a1a] rounded-lg -mx-6 -mb-6 p-5 space-y-5">
                  {/* Stat cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="bg-[#242424] border border-[#333] rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Chain Type</div>
                      <div className="text-white font-semibold text-sm">{selectedChainLog.chainType || 'standard'}</div>
                      {(selectedChainLog.provider || selectedChainLog.model) && (
                        <div className="text-[10px] text-gray-600 mt-1">
                          {[selectedChainLog.provider, selectedChainLog.model].filter(Boolean).join(' / ')}
                        </div>
                      )}
                    </div>
                    <div className="bg-[#242424] border border-[#333] rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Duration</div>
                      <div className="text-white font-semibold text-lg">
                        {selectedChainLog.startTime ? formatDurationMs(selectedChainLog.startTime, selectedChainLog.endTime) : '-'}
                      </div>
                    </div>
                    <div className="bg-[#242424] border border-[#333] rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Entries</div>
                      <div className="text-white font-semibold text-lg">{selectedChainLog.entries?.length || 0}</div>
                    </div>
                    <div className="bg-[#242424] border border-[#333] rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Status</div>
                      <div className={`font-semibold text-lg ${selectedChainLog.finalResponse?.success ? 'text-emerald-400' : selectedChainLog.error ? 'text-red-400' : 'text-gray-400'}`}>
                        {selectedChainLog.finalResponse?.success ? 'Success' : selectedChainLog.error ? 'Failed' : 'N/A'}
                      </div>
                    </div>
                    <div className="bg-[#242424] border border-[#333] rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">Tokens{chainTokenTotals.estimated ? ' (est.)' : ''}</div>
                      <div className="text-white font-semibold text-lg">{(chainTokenTotals.input + chainTokenTotals.output).toLocaleString()}</div>
                      <div className="text-[10px] text-gray-600 mt-1">
                        In: {chainTokenTotals.input.toLocaleString()} | Out: {chainTokenTotals.output.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Performance waterfall */}
                  {(() => {
                    const perfSummary = selectedChainLog.performanceBreakdown?.summary
                    if (!perfSummary) return null
                    const items = Object.entries(perfSummary)
                      .filter(([, v]) => (v as number) > 0)
                      .map(([key, value]) => ({ key, label: key.replace(/Ms$/, '').replace(/([A-Z])/g, ' $1').trim(), ms: value as number }))
                      .sort((a, b) => b.ms - a.ms)
                    if (items.length === 0) return null
                    const maxMs = Math.max(...items.map(i => i.ms), 1)
                    return (
                      <div className="bg-[#242424] border border-[#333] rounded-lg p-4">
                        <h3 className="text-white text-sm font-medium mb-3">Performance Breakdown</h3>
                        <div className="space-y-2">
                          {items.map(item => {
                            const widthPct = Math.max(5, (item.ms / maxMs) * 100)
                            const color = item.ms > 5000 ? 'bg-red-500' : item.ms > 2000 ? 'bg-amber-500' : 'bg-emerald-500'
                            return (
                              <div key={item.key} className="flex items-center gap-2">
                                <div className="w-36 text-right text-xs text-gray-500 flex-shrink-0 capitalize">{item.label}</div>
                                <div className="flex-1 h-5 bg-[#2a2a2a] rounded relative">
                                  <div className={`h-full rounded ${color}`} style={{ width: `${widthPct}%` }} />
                                </div>
                                <div className={`w-16 text-right text-xs flex-shrink-0 ${item.ms > 5000 ? 'text-red-400' : item.ms > 2000 ? 'text-amber-400' : 'text-gray-500'}`}>
                                  {formatMs(item.ms)}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Controls */}
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      value={entryTypeFilter}
                      onChange={e => setEntryTypeFilter(e.target.value)}
                      className="bg-[#2a2a2a] border border-[#444] text-gray-300 text-xs rounded px-2 py-1.5"
                    >
                      <option value="">All types</option>
                      <option value="start">Start</option>
                      <option value="llm_input">LLM Input</option>
                      <option value="llm_output">LLM Output</option>
                      <option value="tag_extracted">Tag Extracted</option>
                      <option value="action">Action</option>
                      <option value="stream_chunk">Stream Chunk</option>
                      <option value="stream_timing">Stream Timing</option>
                      <option value="performance_breakdown">Perf Breakdown</option>
                      <option value="complete">Complete</option>
                      <option value="error">Error</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Search content..."
                      value={entrySearchTerm}
                      onChange={e => setEntrySearchTerm(e.target.value)}
                      className="bg-[#2a2a2a] border border-[#444] text-gray-300 text-xs rounded px-2 py-1.5 w-48"
                    />
                    <span className="text-xs text-gray-600 ml-auto">
                      {filteredEntries.length} / {selectedChainLog.entries?.length || 0} entries
                    </span>
                  </div>

                  {/* Entry timeline */}
                  <div className="space-y-2">
                    {filteredEntries.map((entry: any, index: number) => (
                      <div key={index} className="bg-[#242424] border border-[#333] rounded-lg p-3 hover:border-[#444] transition-colors">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded border ${getEntryTypeBadgeStyle(entry.type)}`}>
                            {entry.type}
                          </span>
                          {(() => {
                            const tc = entry.tokenCounts
                            // Estimate tokens when not available
                            const est = !tc && (entry.type === 'llm_input' || entry.type === 'llm_output')
                              ? {
                                  input: entry.type === 'llm_input' && entry.data?.messages
                                    ? Math.ceil(entry.data.messages.map((m: any) => m.content || '').join(' ').length / 4) : undefined,
                                  output: entry.type === 'llm_output' && entry.data?.response
                                    ? Math.ceil(entry.data.response.length / 4) : undefined,
                                }
                              : null
                            const tokens = tc || est
                            if (!tokens) return null
                            const isEst = !tc && !!est
                            return (
                              <span className={`text-[10px] bg-[#1a1a1a] px-1.5 py-0.5 rounded ml-auto ${isEst ? 'text-gray-700' : 'text-gray-600'}`}>
                                {tokens.input ? `in=${tokens.input.toLocaleString()}` : ''}
                                {tokens.input && tokens.output ? ', ' : ''}
                                {tokens.output ? `out=${tokens.output.toLocaleString()}` : ''}
                                {isEst ? ' ~est' : ''}
                              </span>
                            )
                          })()}
                        </div>
                        <div className="entry-content">
                          {renderEntryContent(entry, index)}
                        </div>
                      </div>
                    ))}
                    {filteredEntries.length === 0 && (
                      <div className="text-center text-gray-600 py-8">No entries match the current filters</div>
                    )}
                  </div>

                  {/* Error display */}
                  {selectedChainLog.error && (
                    <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-4">
                      <h4 className="text-red-400 font-medium text-sm mb-2">Chain Error</h4>
                      <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap overflow-x-auto">
                        {JSON.stringify(selectedChainLog.error, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : selectedConversationId ? (
                /* Chain log file list */
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chain Type</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Entries</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {chainLogFiles.map((file) => (
                      <TableRow
                        key={file.filename}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => loadChainLogDetail(file.filename)}
                      >
                        <TableCell>
                          <Badge variant="outline">{file.chainType || 'standard'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {file.model ? `${file.provider || ''}/${file.model}`.replace(/^\//, '') : '-'}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {file.totalInputTokens || file.totalOutputTokens ? (
                            <span className={file.tokensEstimated ? 'text-muted-foreground' : ''}>
                              {file.totalInputTokens ? `${file.totalInputTokens.toLocaleString()} in` : ''}
                              {file.totalInputTokens && file.totalOutputTokens ? ' / ' : ''}
                              {file.totalOutputTokens ? `${file.totalOutputTokens.toLocaleString()} out` : ''}
                              {file.tokensEstimated ? ' ~' : ''}
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{file.entryCount || '-'}</TableCell>
                        <TableCell className="text-sm font-mono">
                          {file.startTime && file.endTime
                            ? `${((new Date(file.endTime).getTime() - new Date(file.startTime).getTime()) / 1000).toFixed(1)}s`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {file.startTime
                            ? new Date(file.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 } as any)
                            : file.modified ? new Date(file.modified).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>
                          {file.hasError ? (
                            <Badge variant="destructive">Error</Badge>
                          ) : (
                            <Badge variant="secondary">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                /* Conversation list */
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Conversation</TableHead>
                        <TableHead>Log Count</TableHead>
                        <TableHead>Latest</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {chainLogConversations.map((conv) => (
                        <TableRow
                          key={conv.conversationId}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => loadChainLogFiles(conv.conversationId)}
                        >
                          <TableCell className="font-mono text-sm">
                            {conv.conversationId === '_no-thread'
                              ? '(No thread)'
                              : conv.conversationId.substring(0, 8) + '...'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{conv.logCount}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {conv.latestTimestamp
                              ? new Date(conv.latestTimestamp).toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {chainLogConversations.length === 0 && (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No chain logs</h3>
                      <p className="text-muted-foreground">
                        {chainLogStatus?.effectivelyEnabled
                          ? 'No chain logs have been recorded yet. Logs will appear after the user sends a message.'
                          : 'Enable chain logging above to start recording agent chain execution details.'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export { UserDetailPage }
export default UserDetailPage